/**
 * The persistent notice over the live book when a stop or an unsure result
 * has to be said (a newer version took the database, the browser cleared it,
 * or the book on screen may be behind disk). It takes part in the book's fit
 * (ADR-0086, ADR-0083): the full notice while there is room, then one row
 * that is the same height in every case, and below that the "+N more" line,
 * which `ScorebookScreen` draws for every notice.
 *
 * The row keeps what a coach needs and nothing that can be cut: a short form
 * of what happened and the step (a button that opens the full text), the
 * reference code beside it and never cut, and the Reload button. It is not a
 * live region. `NoticeAnnouncer` says the notice, once, and again only when what
 * it says has changed.
 *
 * Reloading goes through the platform adapter, as the full-screen notice's does.
 * The row's buttons are 44px under ADR-0064: each is tapped once, outside the
 * book's tap area, never mid-possession.
 */

import { navigation } from '@good-season/platform';

export interface StopNoticeProps {
  readonly title: string;
  /** The whole body, reference included. */
  readonly body: string;
  /** The one-line form, without the reference. */
  readonly short: string;
  readonly reference: string;
  /** The room is short: one row, not the full notice. */
  readonly oneLine: boolean;
  /** Open the full text. */
  readonly onOpen: () => void;
}

export function StopNotice({ title, body, short, reference, oneLine, onOpen }: StopNoticeProps) {
  const reload = () => {
    navigation.restart();
  };
  if (!oneLine) {
    return (
      <section className="sb__notice">
        <h2 className="sb__noticeTitle">{title}</h2>
        <p className="sb__noticeBody">{body}</p>
        <button className="sb__messageAction sb__noticeAction" onClick={reload} type="button">
          Reload this page
        </button>
      </section>
    );
  }
  return (
    <div className="sb__stop">
      <button
        /* The name starts with what is on the button (WCAG 2.5.3). */
        aria-label={`${short} ${reference}. Open the full notice.`}
        className="sb__stopText"
        onClick={onOpen}
        type="button"
      >
        <span className="sb__stopShort">{short}</span>
        <span className="sb__stopRef">{reference}</span>
        {/* A visible cue that the text opens the full notice. */}
        <span aria-hidden="true" className="sb__stopMore">
          &rsaquo;
        </span>
      </button>
      <button
        aria-label="Reload this page"
        className="sb__stopReload"
        onClick={reload}
        type="button"
      >
        Reload
      </button>
    </div>
  );
}
